import "./UserGroupRoles.css";
import Roles from "../../../core/Roles.json";
import Icon from "../../../components/base/Icon";
import { useEffect, useState } from "react";
import { useHub } from "../../../core/Global";
import JobSource from "../../crm/base/models/JobSource";
import Form from "../../../components/form/Form";
import { toastSuccess } from "../../../components/base/Toast";
import Div from "../../../components/base/Div";
import Indicator from "../../../components/base/Indicator";
import User from "../models/User";
import { userRoleCategories, slugifyCategory } from "../utils/roleCategories";


type Props = {
    user?: User
    categorySlug?: string
}

export default function RolesEditor(props: Props) {

    const hub = useHub()
    const [loading, loadingSet] = useState<boolean>(true)
    const [working, workingSet] = useState<boolean>(false)
    const [sourcesData, sourcesDataSet] = useState<JobSource[]>([])
    const [jobTypesData, jobTypesDataSet] = useState<{ id?: string, name?: string }[]>([])
    const [metrosData, metrosDataSet] = useState<{ id?: string, name?: string }[]>([])
    const [techniciansData, techniciansDataSet] = useState<{ id?: string, name?: string }[]>([])

    const [rolesDefault, rolesDefaultSet] = useState<string[]>();
    const [rolesInherited, rolesInheritedSet] = useState<string[]>();
    const [rolesGranted, rolesGrantedSet] = useState<string[]>();
    const [rolesDenied, rolesDeniedSet] = useState<string[]>();
    const [rolesAllowed, rolesAllowedSet] = useState<string[]>([]);
    const [AllRolesAllowed, AllRolesAllowedSet] = useState<boolean>(false);
    const [search, searchSet] = useState<string>("");

    // the selected category is driven by the route (falls back to the first one)
    const cats = userRoleCategories(props.user?.isTech);
    const effectiveSlug = props.categorySlug ?? cats[0]?.slug;
    const isSources = effectiveSlug == "sources";
    const isJobTypes = effectiveSlug == "job-types";
    const isMetros = effectiveSlug == "metros";
    const isTechnicians = effectiveSlug == "technicians";
    const isRestriction = isSources || isJobTypes || isMetros || isTechnicians;
    const selectedRole: any = isRestriction ? undefined : Roles.find(g => slugifyCategory(g.title) == effectiveSlug);
    const currentTitle = cats.find(c => c.slug == effectiveSlug)?.title ?? (selectedRole?.title ?? "");


    const fetch = () => {
        hub.Get("/accounts/users/roles/" + props.user?.id, res => {
            if (res.success) {

                rolesDefaultSet(res.data.rolesData.rolesDefault);
                rolesInheritedSet(res.data.rolesData.rolesInherited);
                rolesGrantedSet(res.data.rolesData.rolesGranted);
                rolesDeniedSet(res.data.rolesData.rolesDenied);

                rolesAllowedSet(res.data.rolesData.rolesAllowed);
                AllRolesAllowedSet(res.data.rolesData.allRolesAllowed);

                sourcesDataSet(res.data.sourcesData)
                jobTypesDataSet(res.data.jobTypesData ?? [])
                metrosDataSet(res.data.metrosData ?? [])
                techniciansDataSet(res.data.techniciansData ?? [])

                loadingSet(false);
            }
        });
    }
    useEffect(fetch, [])

    // a stale keyword would hide everything after switching category
    useEffect(() => searchSet(""), [props.categorySlug])

    const AddRole = (arr?: string[], value?: string): string[] => {
        if (!value) return [];
        if (!arr) return [value ?? ""];
        var exists = false;
        for (var i = 0; i < arr.length; i++) {
            if (arr[i] == value) {
                exists = true;
                break;
            }
        }

        if (!exists) arr.push(value);
        else arr = RemoveRole(arr, value);

        return arr;

    }

    const RemoveRole = (arr?: string[], value?: string): string[] => {
        if (!arr) return [];
        return arr.filter(function (ele) {
            return ele != value;
        });
    }

    // client-side search over the visible rows (source/metro/... names, or role titles)
    const matchesSearch = (...texts: (string | undefined)[]) => {
        if (window.isNullOrEmpty(search)) return true;
        const q = search.toLowerCase();
        return texts.some(t => (t ?? "").toLowerCase().indexOf(q) > -1);
    }

    const setGrant = (key: string) => {
        var RolesDenied = RemoveRole(rolesDenied, key);
        var RolesGranted = AddRole(rolesGranted, key);
        rolesGrantedSet(r => [...RolesGranted]);
        rolesDeniedSet(r => [...RolesDenied]);
    }

    const setDeny = (key: string) => {
        var RolesGranted = RemoveRole(rolesGranted, key);
        var RolesDenied = AddRole(rolesDenied, key);
        rolesGrantedSet(r => [...RolesGranted]);
        rolesDeniedSet(r => [...RolesDenied]);
    }

    // Renders an access-restriction block (Sources / Job Types / Metros / Technicians):
    // an "All X" grant that, when off, reveals per-item grant/deny rows.
    const renderRestrictionBlock = (allKey: string, prefix: string, allLabel: string, items: { id?: string, name?: string }[]) => {
        const allGranted = !!rolesGranted && rolesGranted.indexOf(allKey) > -1;
        return (
            <tbody>
                {
                    matchesSearch(allLabel) &&
                    <tr>
                        <td>
                            <div className="fw-bold fs-6 ps-2">{allLabel}</div>
                        </td>
                        <td></td>
                        <td>
                            <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                <input className="form-check-input" type="checkbox" disabled
                                    checked={rolesInherited?.includes(allKey)} />
                            </label>
                        </td>
                        <td>
                            <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                <input className="form-check-input" name="grantedRoles" type="checkbox"
                                    onChange={() => setGrant(allKey)}
                                    value={allKey} checked={rolesGranted?.includes(allKey)} />
                            </label>
                        </td>
                        <td></td>
                    </tr>
                }
                {
                    !allGranted && items.filter(it => matchesSearch(it.name)).map((it, k) => {
                        const key = prefix + "-" + it.id;
                        return (
                            <tr key={k}>
                                <td>
                                    <div className="fw-bold fs-6 ps-2">{it.name}</div>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                        <input className="form-check-input" type="checkbox" disabled
                                            checked={rolesDefault?.includes(key)} />
                                    </label>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                        <input className="form-check-input" type="checkbox" disabled
                                            checked={rolesInherited?.includes(key)} />
                                    </label>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                        <input className="form-check-input" name="grantedRoles" type="checkbox"
                                            onChange={() => setGrant(key)}
                                            value={key} checked={rolesGranted?.includes(key)} />
                                    </label>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid form-check-danger"}>
                                        <input className="form-check-input" name="deniedRoles" type="checkbox"
                                            onChange={() => setDeny(key)}
                                            value={key} checked={rolesDenied?.includes(key)} />
                                    </label>
                                </td>
                            </tr>
                        );
                    })
                }
            </tbody>
        );
    }

    return (
        <Form id="roles-form" className="form"
            onSubmit={(body, callback) => {

                workingSet(true);

                hub.Put("/accounts/users/roles/" + props.user?.id, {
                    rolesGranted: rolesGranted,
                    rolesDenied: rolesDenied
                },
                    res => {
                        if (res.success) {
                            toastSuccess("Roles has been updated successfully!");
                        }
                        workingSet(false);
                    });



            }}>
            <div className="card">

                <div className="card-header border-0 grp-roles-header">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0 me-5">Roles &amp; Permissions</h3>
                        <div className="grp-roles-search">
                            <span className="grp-roles-search-icon">
                                <Icon icon="magnifying-glass" size={13} />
                            </span>
                            <input type="text" className="form-control form-control-sm"
                                placeholder={`Search in ${currentTitle}...`}
                                value={search}
                                onChange={e => searchSet(e.target.value)} />
                        </div>
                    </div>
                    <div className="card-action">
                        <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                            {
                                working ?
                                    <>
                                        <Icon icon="spinner-third" spin />
                                        <span className="ms-2">Updating...</span>
                                    </>
                                    :
                                    <>Update Roles</>
                            }
                        </button>
                    </div>
                </div>

                <div className="card-body border-top ">


                    <Div className="my-20" visible={loading}>
                        <Indicator />
                    </Div>

                    <Div visible={!loading}>
                        <table className="table table-striped table-roles">
                            <thead>
                                <tr>
                                    <th>
                                        <div className="fs-4 fw-bolder">
                                            {currentTitle}
                                        </div>
                                    </th>
                                    <th className="action-col w-125px">
                                        Default
                                    </th>
                                    <th className="action-col w-125px">
                                        Inherited
                                    </th>
                                    <th className="action-col w-125px">
                                        Grant
                                    </th>
                                    <th className="action-col w-125px">
                                        Deny
                                    </th>
                                </tr>
                            </thead>

                            {
                                isSources && renderRestrictionBlock("SourcesAll", "Sources", "All Sources", sourcesData)
                            }

                            {
                                isJobTypes && renderRestrictionBlock("JobTypesAll", "JobTypes", "All Job Types", jobTypesData)
                            }

                            {
                                isMetros && renderRestrictionBlock("MetrosAll", "Metros", "All Metros", metrosData)
                            }

                            {
                                isTechnicians && renderRestrictionBlock("TechniciansAll", "Technicians", "All Technicians", techniciansData)
                            }

                            {
                                selectedRole &&
                                <tbody>
                                    {
                                        selectedRole.roles
                                            .filter((f: any) => AllRolesAllowed || rolesAllowed.indexOf(f.roleName) > -1)
                                            .filter((f: any) => matchesSearch(f.title, f.description))
                                            .map((role: any, roleKey: number) =>
                                                <tr key={roleKey}>
                                                    <td>
                                                        <div className="fw-bold fs-6">
                                                            {role.title}
                                                        </div>
                                                        <div className={`fs-8 ${(role.danger ? "text-danger" : "text-muted")}`}>
                                                            {role.description}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                                            <input className="form-check-input" type="checkbox" disabled
                                                                checked={rolesDefault?.includes(role.roleName)} />
                                                        </label>
                                                    </td>
                                                    <td>
                                                        <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                                            <input className="form-check-input" type="checkbox" disabled
                                                                checked={rolesInherited?.includes(role.roleName)} />
                                                        </label>
                                                    </td>
                                                    <td>
                                                        <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                                            <input className="form-check-input" name="grantedRoles" type="checkbox"
                                                                onChange={e => {

                                                                    var RolesDenied = RemoveRole(rolesDenied, role.roleName);
                                                                    var RolesGranted = AddRole(rolesGranted, role.roleName);

                                                                    rolesGrantedSet(r => [...RolesGranted]);
                                                                    rolesDeniedSet(r => [...RolesDenied]);

                                                                }}
                                                                value={role.roleName} checked={rolesGranted?.includes(role.roleName)} />
                                                        </label>
                                                    </td>
                                                    <td>
                                                        <label className={"form-check form-switch form-check-custom  form-check-solid form-check-danger"}>
                                                            <input className="form-check-input" name={"deniedRoles"} type="checkbox"
                                                                onChange={e => {

                                                                    var RolesGranted = RemoveRole(rolesGranted, role.roleName);
                                                                    var RolesDenied = AddRole(rolesDenied, role.roleName);

                                                                    rolesGrantedSet(r => [...RolesGranted]);
                                                                    rolesDeniedSet(r => [...RolesDenied]);

                                                                }}
                                                                value={role.roleName} checked={rolesDenied?.includes(role.roleName)} />
                                                        </label>
                                                    </td>
                                                </tr>
                                            )
                                    }
                                </tbody>
                            }

                        </table>

                        {/* <div className="fs-4 fw-bolder me-2 mt-10">
                            Allowed Job Statuses 
                        </div>
                        <div>

                        </div> */}


                    </Div>

                </div>



            </div>
        </Form>
    )
}